import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {Loop, ArrowDropUp, ArrowDropDown} from '@material-ui/icons';
import {
  makeStyles,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import './SurgeonStyle.scss';
import MaterialTable, {Icons} from 'material-table';
import Pagination from '@material-ui/lab/Pagination';
import SearchField from '../../components/search/SearchField';
import AppLoader from '../../components/apploader/AppLoader';
import {axios, CancelTokenSource} from '../../../../shared/src/axios';
import EditSurgeon from './EditSurgeon/EditSurgeon';
import {
  getAllSurgeons,
  getServiceLines,
} from '../../../../shared/src/services/SurgeonService';
import {_snackbarAcknowledgement} from '../../../../shared/src/store/actions/AuthActions';
import {useDispatch} from 'react-redux';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiToolbar-regular': {
      minHeight: 0,
    },
  },
  customTooltip: {
    fontSize: '1rem',
  },
}));

const paginationStyles = makeStyles(theme => ({
  root: {
    '& > *': {
      marginTop: theme.spacing(2),
    },
  },
}));

interface SurgeonData {
  id: string;
  name: string;
  serviceLine: string;
  surgeonId: string;
}

interface ServiceLineOption {
  id: string;
  label: string;
}

const ITEMS_PER_PAGE = 8;

const Surgeon = (): JSX.Element => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const pageStyles = paginationStyles();
  const [allSurgeonsData, setAllSurgeonsData] = useState<SurgeonData[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [selectedSurgeonId, setSelectedSurgeonId] = useState<string | null>(
    null
  );
  const [serviceLines, setServiceLines] = useState<ServiceLineOption[]>([]);
  const [selectedServiceLine, setSelectedServiceLine] = useState<string>('');
  const offset = useRef<number>(0);
  const searchTextRef = useRef('');
  const searchTimeout = useRef<number | undefined>(undefined);
  const [loader, setLoader] = useState({
    getDetailsLoader: false,
  });
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  useEffect(() => {
    if (selectedSurgeonId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedSurgeonId]);

  const getDetails = async (
    offValue: number,
    searchText?: string,
    serviceLine?: string,
    cancelToken?: CancelTokenSource['token']
  ): Promise<void> => {
    try {
      setLoader(prev => ({...prev, getDetailsLoader: true}));
      const surgeons = await getAllSurgeons(
        offValue * ITEMS_PER_PAGE,
        serviceLine,
        searchText,
        cancelToken
      );
      if (surgeons?.data?.rows) {
        setAllSurgeonsData(surgeons.data.rows);
        setPageCount(Math.ceil(surgeons.data.count / ITEMS_PER_PAGE));
      }
    } catch (error) {
      // Handle error silently or use a proper error handling mechanism
      setAllSurgeonsData([]);
      setPageCount(0);
    } finally {
      setLoader(prev => ({...prev, getDetailsLoader: false}));
    }
  };

  const getServiceLineDetails = useCallback(async (): Promise<void> => {
    try {
      const response = await getServiceLines();
      setServiceLines(response.data.service_lines);
    } catch (error) {
      const err = error as any;
      dispatch(_snackbarAcknowledgement(true, err.message, 'error'));
    }
  }, [dispatch]);

  useEffect(() => {
    getDetails(0);
    getServiceLineDetails();
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
    };
  }, [getServiceLineDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchText = (event: any): void => {
    const searchText = event.target.value;
    searchTextRef.current = searchText;

    if (searchTimeout.current) {
      window.clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = window.setTimeout(() => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Operation canceled due to new request.');
      }
      cancelTokenRef.current = axios.CancelToken.source();
      getDetails(
        offset.current,
        searchText,
        selectedServiceLine,
        cancelTokenRef.current.token
      );
    }, 500);
  };

  const handlePagination = (event: any, page: number) => {
    offset.current = page - 1;
    getDetails(offset.current, searchTextRef.current);
  };

  const handleRowClick = (
    event?: React.MouseEvent<Element, MouseEvent>,
    rowData?: SurgeonData,
    toggleDetailPanel?: (panelIndex?: number) => void
  ): void => {
    if (rowData) {
      setSelectedSurgeonId(rowData.id);
    }
  };

  const handleCloseDialog = (refreshPage?: boolean) => {
    setSelectedSurgeonId(null);
    if (refreshPage) {
      getDetails(offset.current, searchTextRef.current, selectedServiceLine);
    }
  };

  const handleServiceLineChange = (
    event: React.ChangeEvent<{value: unknown}>
  ) => {
    const value = event.target.value as string;
    setSelectedServiceLine(value);
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Operation canceled due to new request.');
    }
    cancelTokenRef.current = axios.CancelToken.source();
    getDetails(
      offset.current,
      searchTextRef.current,
      value,
      cancelTokenRef.current.token
    );
  };

  const stylesObject = {
    width: '100%',
    fontFamily: 'hindGunturMedium',
    fontSize: '0.9vw',
  };

  const optionsObject = {
    search: false,
    paging: false,
    header: true,
    showTitle: false,
    sorting: true,
    headerStyle: {
      color: '#777B7E',
      fontFamily: 'workSansSemiBold',
      height: '3vw',
      fontSize: '0.75vw',
    },
    rowStyle: {
      cursor: 'pointer',
    },
  };

  const tableIcons: Partial<Icons> = {
    SortArrow: React.forwardRef<SVGSVGElement, {className?: string}>(
      (props, ref) =>
        props.className?.includes('MuiTableSortLabel-active') ? (
          props.className?.includes('MuiTableSortLabel-iconDirectionDesc') ? (
            <ArrowDropDown
              {...props}
              ref={ref}
              style={{color: '#000000', fontSize: '1.5rem'}}
            />
          ) : (
            <ArrowDropUp
              {...props}
              ref={ref}
              style={{color: '#000000', fontSize: '1.5rem'}}
            />
          )
        ) : (
          <ArrowDropUp
            {...props}
            ref={ref}
            style={{color: '#777B7E', fontSize: '1.5rem', opacity: 0.5}}
          />
        )
    ),
  };

  return (
    <div className="surgeon-container">
      <div className="surgeon-view">
        <div className="surgeon-text">{t('SURGEONS.TITLE')}</div>
        <div className="search-main-view">
          <div className="refresh-container">
            <Tooltip
              title={t('Refresh')}
              placement="top"
              classes={{tooltip: classes.customTooltip}}>
              <Loop
                className="refresh-icon"
                onClick={() =>
                  getDetails(offset.current, searchTextRef.current)
                }
              />
            </Tooltip>
          </div>
          <div
            className={`search-width ${
              !loader.getDetailsLoader && 'search-index'
            }`}>
            <SearchField
              placeholder={t('SURGEONS.SEARCH_PLACE_HOLDER_TEXT')}
              onChange={handleSearchText}
            />
          </div>
          <div className="service-line-filter">
            <FormControl variant="outlined" size="small">
              <InputLabel>
                {t('SURGEONS.SERVICE_LINE_DROPDOWN_LABEL')}
              </InputLabel>
              <Select
                value={selectedServiceLine}
                onChange={handleServiceLineChange}
                label={t('SURGEONS.SERVICE_LINE_DROPDOWN_LABEL')}
                style={{minWidth: 200}}>
                <MenuItem value="">
                  <em>{t('None')}</em>
                </MenuItem>
                {serviceLines.map(line => (
                  <MenuItem key={line.id} value={line.id}>
                    {line.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      {!loader.getDetailsLoader && (
        <div className="table-container">
          <MaterialTable
            icons={tableIcons}
            style={stylesObject}
            data={allSurgeonsData}
            columns={[
              {
                title: t('SURGEONS.SURGEON_NAME'),
                field: 'name',
                headerStyle: {textAlign: 'left'},
                defaultSort: 'asc',
                sorting: true,
              },
              {
                title: t('SURGEONS.SERVICE_LINE'),
                field: 'service_line_name',
                headerStyle: {textAlign: 'left'},
                sorting: true,
              },
              {
                title: t('SURGEONS.SURGEON_ID'),
                field: 'surgeon_id',
                headerStyle: {textAlign: 'left'},
                sorting: true,
              },
            ]}
            options={optionsObject}
            onRowClick={handleRowClick}
            components={{
              Container: props => <div {...props} className={classes.root} />,
            }}
          />
        </div>
      )}

      {pageCount > 0 && (
        <div
          className={`pagination-view ${
            loader.getDetailsLoader && 'pagination-display'
          }`}>
          <div className={pageStyles.root}>
            <Pagination
              count={pageCount}
              shape="rounded"
              onChange={handlePagination}
              page={offset.current + 1}
            />
          </div>
        </div>
      )}

      {loader.getDetailsLoader && <AppLoader />}

      <EditSurgeon
        open={!!selectedSurgeonId}
        onClose={handleCloseDialog}
        surgeonId={selectedSurgeonId || ''}
      />
    </div>
  );
};

export default Surgeon;
